import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  MyLocation as MyLocationIcon,
  Place as PlaceIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getDistance } from 'geolib';
import { logEvent } from 'firebase/analytics';
import { getFavoriteUpdateStatus } from '../utils/favoriteAccess';
import { useNavigate } from 'react-router-dom';
import CourtCardsSkeleton from './CourtCardsSkeleton';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 var(--cc-focus); }
  50% { box-shadow: 0 0 20px 4px var(--cc-hover); }
`;

// Time slots - generate all possible hours (6am to midnight)
const generateTimeSlots = (startHour, endHour) => {
  const times = [];
  for (let h = startHour; h < endHour; h++) {
    times.push(`${String(h).padStart(2, '0')}:00`);
  }
  return times;
};

// Default schedule (9am - 11pm)
const DEFAULT_SCHEDULE = {
  weekday: { start: 9, end: 23 },
  saturday: { start: 9, end: 23 },
  sunday: { start: 9, end: 23 }
};

const ALL_TIMES = generateTimeSlots(6, 24); // All possible times for data lookup
const TIME_SLOT_WIDTH = 48;
const TIME_SLOT_GAP = 3;
const TIME_WINDOW_SIZE = 6;
const PERIODS = [
  { key: 'morning', labelKey: 'morning', start: 6, end: 12 },
  { key: 'afternoon', labelKey: 'afternoon', start: 12, end: 18 },
  { key: 'evening', labelKey: 'evening', start: 18, end: 24 }
];

export const getTorontoDateKey = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day}`;
};

const addDaysToDateKey = (dateKey, daysToAdd) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + daysToAdd);
  return [date.getUTCFullYear(), String(date.getUTCMonth() + 1).padStart(2, '0'), String(date.getUTCDate()).padStart(2, '0')].join('-');
};

export const getUpcomingDateKeys = (courts, todayDateKey, limit = 5) => {
  const dates = new Set();
  courts.forEach(court => {
    court.tableStats?.forEach(facility => {
      Object.keys(facility.availability || {}).forEach(date => dates.add(date));
    });
  });

  return Array.from(dates)
    .filter(date => date >= todayDateKey)
    .sort()
    .slice(0, limit);
};

// Compare current Toronto wall-clock time against an unzoned ET ISO string
// (e.g. "2026-05-07T10:00:00").
const getTorontoNowString = () => {
  if (typeof window !== 'undefined' && window.__predictiveNowOverride) {
    return window.__predictiveNowOverride;
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(new Date()).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  let hour = parts.hour;
  if (hour === '24') hour = '00';
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`;
};

const isPredictiveSlotVisible = (slot) => {
  const showAt = slot?.predictiveDisplay?.showAt;
  if (!showAt) return true;
  return getTorontoNowString() >= showAt;
};

const normalizeCourtSearchValue = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

// Get schedule for a court based on its name
const getCourtSchedule = (courtName, schedules) => {
  if (!schedules || !courtName) return DEFAULT_SCHEDULE;

  // Find matching schedule by checking if court name contains the pattern
  for (const schedule of schedules) {
    if (schedule.pattern && courtName.toLowerCase().includes(schedule.pattern.toLowerCase())) {
      return {
        weekday: schedule.weekday || DEFAULT_SCHEDULE.weekday,
        saturday: schedule.saturday || DEFAULT_SCHEDULE.saturday,
        sunday: schedule.sunday || DEFAULT_SCHEDULE.sunday
      };
    }
  }
  return DEFAULT_SCHEDULE;
};

// Get time slots for a court on a specific date
const getCourtTimes = (courtName, dateString, schedules) => {
  const schedule = getCourtSchedule(courtName, schedules);
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();

  let daySchedule;
  if (dayOfWeek === 0) {
    daySchedule = schedule.sunday;
  } else if (dayOfWeek === 6) {
    daySchedule = schedule.saturday;
  } else {
    daySchedule = schedule.weekday;
  }

  return generateTimeSlots(daySchedule.start, daySchedule.end);
};

export const formatDateParts = (dateString, isFrench, todayDateKey = getTorontoDateKey()) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  const days = isFrench
    ? ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM']
    : ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const months = isFrench
    ? ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUN', 'JUL', 'AOÛ', 'SEP', 'OCT', 'NOV', 'DÉC']
    : ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  const isToday = dateString === todayDateKey;
  const isTomorrow = dateString === addDaysToDateKey(todayDateKey, 1);

  return {
    dayName: days[date.getDay()],
    dayNum: String(day),
    month: months[month - 1],
    isToday,
    isTomorrow
  };
};

export const AvailabilityLegend = ({ isFrench = false }) => {
  const steps = [
    { label: '1', color: 'var(--cc-avail-1-bg)', border: 'var(--cc-avail-1-border)' },
    { label: '2–3', color: 'var(--cc-avail-2-bg)', border: 'var(--cc-avail-2-border)' },
    { label: '4–5', color: 'var(--cc-avail-3-bg)', border: 'var(--cc-avail-3-border)' },
    { label: '6+', color: 'var(--cc-avail-4-bg)', border: 'var(--cc-avail-4-border)' }
  ];

  return (
    <Box
      className="availability-legend"
      aria-label={isFrench ? 'Légende des disponibilités' : 'Availability legend'}
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
    >
      <Typography sx={{ color: 'var(--cc-text-soft)', fontSize: '0.75rem', fontWeight: 700 }}>
        {isFrench ? 'Terrains libres' : 'Open courts'}
      </Typography>
      {steps.map(step => (
        <Box key={step.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 18,
              height: 18,
              borderRadius: '5px',
              backgroundColor: step.color,
              border: `1px solid ${step.border}`
            }}
          />
          <Typography sx={{ color: 'var(--cc-text-soft)', fontSize: '0.7rem', fontWeight: 700 }}>
            {step.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const translations = {
  en: {
    myCourts: "My Courts",
    noFavorites: "No favorites yet",
    addFirst: "Star your favorite courts from the Search tab to track them here",
    addCourts: "Add Courts",
    searchCourts: "Search courts...",
    available: "available",
    noAvailability: "No availability",
    signIn: "Sign in to track favorites",
    signInDesc: "Save your favorite courts and see all availability at a glance",
    login: "Sign In",
    chooseCourt: "Choose a court",
    noCourtsAt: "No courts at this time",
    back: "Back",
    customize: "Customize",
    availabilitySetup: "Time of day",
    availabilityDesc: "Pick the times you usually play so we can focus your timeline.",
    weekdays: "Weekdays",
    weekends: "Weekends",
    timeOfDay: "Time of day",
    on: "On",
    off: "Off",
    morning: "Morning",
    afternoon: "Afternoon",
    evening: "Evening",
    save: "Save",
    cancel: "Cancel",
    earlier: "Earlier",
    later: "Later",
    useMyLocation: "Use my location",
    locating: "Locating...",
    editOrder: "Edit order",
    done: "Done",
    saveOrder: "Save order",
    dragToReorder: "Drag courts to set your custom order",
    todayShort: "Today",
    favoriteLimitTitle: "Favorite limit reached",
    favoriteLimitMessage: "Free accounts can save 1 favorite court. Upgrade to Premium to add more courts to My Courts.",
    upgrade: "Upgrade",
    close: "Close",
    predictiveTitle: "Predictive link (Premium)",
    predictiveNotice: "This time slot is predicted from past booking patterns. The court isn't released for booking yet, so availability isn't guaranteed.",
    openPredictive: "Open predictive link",
    predictiveUpgradeTitle: "Premium-only predictive link",
    predictiveUpgradeBody: "Predictive links open booking pages before the city officially releases them, giving Premium members a head start. Upgrade to unlock these slots."
  },
  fr: {
    myCourts: "Mes Terrains",
    noFavorites: "Aucun favori",
    addFirst: "Ajoutez des favoris depuis l'onglet Recherche pour les suivre ici",
    addCourts: "Ajouter",
    searchCourts: "Rechercher...",
    available: "dispo",
    noAvailability: "Aucune disponibilité",
    signIn: "Connectez-vous",
    signInDesc: "Sauvegardez vos terrains favoris et voyez les disponibilités",
    login: "Connexion",
    chooseCourt: "Choisir un terrain",
    noCourtsAt: "Aucun terrain à cette heure",
    back: "Retour",
    customize: "Personnaliser",
    availabilitySetup: "Moment de la journée",
    availabilityDesc: "Choisissez vos heures pour personnaliser votre timeline.",
    weekdays: "Semaine",
    weekends: "Fin de semaine",
    timeOfDay: "Moments de la journee",
    on: "Oui",
    off: "Non",
    morning: "Matin",
    afternoon: "Après-midi",
    evening: "Soir",
    save: "Enregistrer",
    cancel: "Annuler",
    earlier: "Plus tôt",
    later: "Plus tard",
    useMyLocation: "Utiliser ma position",
    locating: "Localisation...",
    editOrder: "Reorganiser",
    done: "Termine",
    saveOrder: "Enregistrer l'ordre",
    dragToReorder: "Glissez les terrains pour choisir l'ordre",
    todayShort: "Auj.",
    favoriteLimitTitle: "Limite de favoris atteinte",
    favoriteLimitMessage: "Les comptes gratuits peuvent sauvegarder 1 terrain favori. Passez au forfait Premium pour ajouter plus de terrains.",
    upgrade: "Mettre à niveau",
    close: "Fermer",
    predictiveTitle: "Lien prédictif (Premium)",
    predictiveNotice: "Ce créneau est prédit à partir des réservations passées. Le terrain n'est pas encore ouvert à la réservation, la disponibilité n'est pas garantie.",
    openPredictive: "Ouvrir le lien prédictif",
    predictiveUpgradeTitle: "Lien prédictif réservé aux membres Premium",
    predictiveUpgradeBody: "Les liens prédictifs ouvrent les pages de réservation avant la mise en ligne officielle, ce qui donne une longueur d'avance aux membres Premium. Passez à Premium pour débloquer ces créneaux."
  }
};

const PersonalizedCourtsView = ({ isFrench = false, analytics, auth, accountType, updateLastRefreshTime, styleguideData = null, todayOverride = null }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(!styleguideData);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );
  const [allCourts, setAllCourts] = useState(styleguideData?.allCourts || []);
  const [favorites, setFavoritesState] = useState(styleguideData?.favorites || []);
  const [quickLinks, setQuickLinks] = useState(styleguideData?.quickLinks || null);
  const [predictiveLinks, setPredictiveLinks] = useState(styleguideData?.predictiveLinks || null);
  const [predictiveConfirm, setPredictiveConfirm] = useState(null);
  const [showPredictiveUpgrade, setShowPredictiveUpgrade] = useState(false);
  const [courtSchedules, setCourtSchedules] = useState(styleguideData?.courtSchedules || []);
  const [selectedDay, setSelectedDay] = useState(0);
  const [todayDateKey, setTodayDateKey] = useState(() => todayOverride || getTorontoDateKey());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showPrefsPanel, setShowPrefsPanel] = useState(false);
  const [periodPrefs, setPeriodPrefs] = useState({ weekday: 1, weekend: 1 });
  const [activePeriodIndex, setActivePeriodIndex] = useState(1);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [draggingCourtId, setDraggingCourtId] = useState(null);
  const [dragMetrics, setDragMetrics] = useState(null);
  const [showFavoriteLimitDialog, setShowFavoriteLimitDialog] = useState(false);
  const favoritesRef = useRef(favorites);
  const dragOrderChangedRef = useRef(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const t = translations[isFrench ? 'fr' : 'en'];
  const canCustomizeOrder = Boolean(accountType) && accountType !== 'free' && favorites.length > 1;

  useEffect(() => {
    favoritesRef.current = favorites;
  }, [favorites]);

  useEffect(() => {
    if (!canCustomizeOrder) setIsEditingOrder(false);
  }, [canCustomizeOrder]);

  useEffect(() => {
    if (todayOverride) {
      setTodayDateKey(todayOverride);
      return undefined;
    }

    const syncTorontoDate = () => {
      setTodayDateKey(current => {
        const next = getTorontoDateKey();
        return current === next ? current : next;
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncTorontoDate();
    };

    const intervalId = window.setInterval(syncTorontoDate, 60 * 1000);
    window.addEventListener('focus', syncTorontoDate);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncTorontoDate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [todayOverride]);

  useEffect(() => {
    setSelectedDay(0);
  }, [todayDateKey]);

  const handleGetLocation = async () => {
    if (!("geolocation" in navigator)) return;
    setIsLoadingLocation(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000, enableHighAccuracy: false, maximumAge: 300000
        });
      });
      const location = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      setUserLocation(location);

      // Update courts with distance
      setAllCourts(prev => prev.map(court => {
        if (court.sitePosition) {
          return { ...court, distanceInKm: (getDistance(location, court.sitePosition) / 1000).toFixed(1) };
        }
        return court;
      }).sort((a, b) => (parseFloat(a.distanceInKm) || 999) - (parseFloat(b.distanceInKm) || 999)));
    } catch (e) {
      console.error('Location error:', e);
    }
    setIsLoadingLocation(false);
  };


  // Load data with auto-refresh
  useEffect(() => {
    if (styleguideData) {
      setIsLoading(false);
      return undefined;
    }

    const fetchData = async (isInitial = false) => {
      if (isInitial) {
        setIsLoading(true);
        setLoadFailed(false);
      }
      const firestore = firebase.firestore();

      try {
        const [overviewSnapshot, quickLinksSnapshot, predictiveLinksSnapshot, schedulesSnapshot] = await Promise.all([
          firestore.collection('overview').doc('loadedSitesTable').get(),
          firestore.collection('overview').doc('quickLinks').get(),
          firestore.collection('overview').doc('predictiveLinks').get(),
          firestore.collection('courtSchedules').get()
        ]);

        const snapShotData = overviewSnapshot.data();
        if (quickLinksSnapshot.exists) {
          setQuickLinks(quickLinksSnapshot.data().quickLinks);
        }
        if (predictiveLinksSnapshot.exists) {
          setPredictiveLinks(predictiveLinksSnapshot.data().predictiveLinks || {});
        } else {
          setPredictiveLinks({});
        }

        // Load court schedules
        const schedules = [];
        schedulesSnapshot.forEach(doc => {
          schedules.push({ id: doc.id, ...doc.data() });
        });
        setCourtSchedules(schedules);

        if (!snapShotData) {
          if (isInitial) {
            setLoadFailed(true);
            setIsLoading(false);
          }
          return;
        }

        // Geolocation fetched in background - don't block loading
        let userLocation = null;

        const updatedData = Object.entries(snapShotData).map(([doc, courtData]) => {
          const court = { ...courtData, id: doc, distanceInKm: null };
          if (userLocation && court.sitePosition) {
            court.distanceInKm = (getDistance(userLocation, court.sitePosition) / 1000).toFixed(1);
          }
          return court;
        });

        updatedData.sort((a, b) => (parseFloat(a.distanceInKm) || 999) - (parseFloat(b.distanceInKm) || 999));
        setAllCourts(updatedData);

        // Update the last refresh time in footer
        if (updateLastRefreshTime) updateLastRefreshTime();
      } catch (error) {
        console.error('Error:', error);
        if (isInitial) setLoadFailed(true);
      }
      if (isInitial) setIsLoading(false);
    };

    fetchData(true);

    // Auto-refresh every 10 minutes
    const refreshInterval = setInterval(() => fetchData(false), 600000);
    return () => clearInterval(refreshInterval);
  }, [styleguideData, updateLastRefreshTime, loadAttempt]);

  useEffect(() => {
    if (styleguideData) return undefined;
    const retryWhenOnline = () => {
      setIsOffline(false);
      setLoadAttempt(current => current + 1);
    };
    const showOfflineState = () => setIsOffline(true);
    window.addEventListener('online', retryWhenOnline);
    window.addEventListener('offline', showOfflineState);
    return () => {
      window.removeEventListener('online', retryWhenOnline);
      window.removeEventListener('offline', showOfflineState);
    };
  }, [styleguideData]);

  // Load favorites
  useEffect(() => {
    if (styleguideData) return undefined;
    const user = firebase.auth().currentUser;
    if (user) {
      const unsubscribe = firebase.firestore()
        .collection('userFavorites').doc(user.uid)
        .onSnapshot((doc) => {
          if (doc.exists) setFavoritesState(doc.data().favorites || []);
        });
      return () => unsubscribe();
    }
  }, [auth, styleguideData]);

  // Load period preferences from Firestore
  useEffect(() => {
    if (styleguideData) return undefined;
    const user = firebase.auth().currentUser;
    if (user) {
      firebase.firestore().collection('userPreferences').doc(user.uid).get()
        .then((doc) => {
          if (doc.exists && doc.data().myCourtsPrefs) {
            const prefs = doc.data().myCourtsPrefs;
            setPeriodPrefs({ weekday: prefs.weekday ?? 1, weekend: prefs.weekend ?? 1 });
            const today = new Date();
            const isWeekend = today.getDay() === 0 || today.getDay() === 6;
            setActivePeriodIndex(isWeekend ? (prefs.weekend ?? 1) : (prefs.weekday ?? 1));
          }
        })
        .catch((e) => console.error('Error loading prefs:', e));
    }
  }, [auth, styleguideData]);

  const setFavorites = useCallback(async (newFavorites) => {
    if (styleguideData) {
      setFavoritesState(newFavorites);
      return;
    }
    const user = firebase.auth().currentUser;
    if (!user) return;
    const updateStatus = getFavoriteUpdateStatus(accountType, newFavorites);
    if (updateStatus === 'account-loading') return;
    if (updateStatus === 'limit-reached') {
      setShowFavoriteLimitDialog(true);
      if (analytics) {
        logEvent(analytics, 'favorite_limit_reached', {
          currentCount: favoritesRef.current.length,
          attemptedCount: newFavorites.length,
          source: 'my_courts'
        });
      }
      return;
    }
    try {
      await firebase.firestore().collection('userFavorites').doc(user.uid).set({ favorites: newFavorites });
      setFavoritesState(newFavorites);
      if (analytics) logEvent(analytics, 'favorite_updated', { count: newFavorites.length });
    } catch (e) { console.error(e); }
  }, [accountType, analytics, styleguideData]);

  // Get available dates
  const availableDates = useMemo(() => {
    return getUpcomingDateKeys(allCourts, todayDateKey);
  }, [allCourts, todayDateKey]);

  const userIsPremium = Boolean(accountType) && accountType !== 'free';

  // Process favorite courts with availability by time
  const favoriteCourts = useMemo(() => {
    const targetDate = availableDates[selectedDay];
    if (!targetDate) return [];

    const courtsById = new Map(allCourts.map(court => [parseInt(court.siteId), court]));

    return favorites
      .map(favId => courtsById.get(parseInt(favId)))
      .filter(Boolean)
      .map(court => {
        // Get court-specific times based on schedule
        const courtTimes = getCourtTimes(court.siteName, targetDate, courtSchedules);

        // Build availability map for selected date
        const timeSlots = {};
        ALL_TIMES.forEach(t => { timeSlots[t] = []; });

        if (court.tableStats) {
          court.tableStats.forEach(facility => {
            const daySlots = facility.availability?.[targetDate] || [];
            daySlots.forEach(slot => {
              if (!timeSlots[slot.startTime]) return;

              const searchLinkKey = slot.searchLinkKey
                || facility.searchLinks?.[targetDate]?.[slot.startTime]
                || facility.searchLinks?.[targetDate]?.['all'];

              const normalUrl = quickLinks?.[slot.linkKey];
              const predictive = predictiveLinks?.[slot.linkKey];

              let isPredictive = false;
              if (normalUrl) {
                // Normal slot — keep as-is.
              } else if (
                slot.isPredictive
                && predictive
                && isPredictiveSlotVisible(slot)
              ) {
                // Show predictive slots to everyone; free users get an upgrade
                // prompt when they click instead of the actual booking link.
                isPredictive = true;
              } else {
                // No usable link — drop the slot from the visible grid.
                return;
              }

              timeSlots[slot.startTime].push({
                facility: facility.facilityName,
                linkKey: slot.linkKey,
                searchLinkKey,
                isPredictive
              });
            });
          });
        }

        const availableCount = Object.values(timeSlots).filter(s => s.length > 0).length;
        const hasPredictive = Object.values(timeSlots).some(s => s.some(slot => slot.isPredictive));
        return { ...court, timeSlots, availableCount, date: targetDate, courtTimes, hasPredictive };
      });
  }, [allCourts, favorites, availableDates, selectedDay, courtSchedules, quickLinks, predictiveLinks, userIsPremium]);

  const getSlotLink = (slot) => {
    const key = slot.linkKey || slot.searchLinkKey;
    if (!key) return null;
    if (slot.isPredictive) {
      return predictiveLinks?.[key]?.url || null;
    }
    return quickLinks?.[key] || null;
  };

  // Get window times for a specific court (filtered by period on mobile)
  const getWindowTimesForCourt = (courtTimes) => {
    if (isDesktop) {
      return courtTimes;
    }
    const period = PERIODS[activePeriodIndex] || PERIODS[1];
    return courtTimes.filter(time => {
      const hour = parseInt(time.split(':')[0], 10);
      return hour >= period.start && hour < period.end;
    });
  };

  const getAvailabilityColor = (count) => {
    if (count >= 6) return { bg: 'var(--cc-avail-4-bg)', border: 'var(--cc-avail-4-border)' };
    if (count >= 4) return { bg: 'var(--cc-avail-3-bg)', border: 'var(--cc-avail-3-border)' };
    if (count >= 2) return { bg: 'var(--cc-avail-2-bg)', border: 'var(--cc-avail-2-border)' };
    return { bg: 'var(--cc-avail-1-bg)', border: 'var(--cc-avail-1-border)' };
  };

  const getNowMarkerPercent = (times) => {
    if (!times.length) return null;
    const [startHour] = times[0].split(':').map(Number);
    const [endHour] = times[times.length - 1].split(':').map(Number);
    const startMinutes = startHour * 60;
    const endMinutes = (endHour + 1) * 60;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    if (nowMinutes < startMinutes || nowMinutes > endMinutes) return null;
    const fraction = (nowMinutes - startMinutes) / (endMinutes - startMinutes);
    return fraction * 100;
  };

  const handleTileClick = (event, court, time) => {
    const slots = court.timeSlots[time] || [];
    if (slots.length === 0) return;

    // Always show selection popover, even for single court
    setSelectedSlot({
      court,
      time,
      slots,
      anchorEl: event.currentTarget
    });
  };

  const closeSelection = () => setSelectedSlot(null);

  const handleCourtSelect = (slot) => {
    if (analytics) logEvent(analytics, 'my_courts_book_court', {
      court: selectedSlot?.court.siteName,
      time: selectedSlot?.time,
      predictive: !!slot.isPredictive
    });
    if (slot.isPredictive && !userIsPremium) {
      if (analytics) logEvent(analytics, 'predictive_upgrade_prompt', {
        court: selectedSlot?.court.siteName,
        time: selectedSlot?.time,
        source: 'my_courts'
      });
      setShowPredictiveUpgrade(true);
      closeSelection();
      return;
    }
    const link = getSlotLink(slot);
    if (!link) {
      closeSelection();
      return;
    }
    if (slot.isPredictive) {
      setPredictiveConfirm({
        url: link,
        facility: slot.facility,
        court: selectedSlot?.court,
        time: selectedSlot?.time
      });
      closeSelection();
      return;
    }
    window.open(link, '_blank');
    closeSelection();
  };

  const handleRemoveFavorite = (e, courtId) => {
    e.stopPropagation();
    setFavorites(favorites.filter(id => id !== parseInt(courtId)));
  };

  const handleAddFavorite = (courtId) => {
    setFavorites([...favorites, parseInt(courtId)]);
    setShowAddDialog(false);
    setSearchQuery('');
  };

  const reorderFavorite = useCallback((dragCourtId, targetCourtId) => {
    const dragId = parseInt(dragCourtId);
    const targetId = parseInt(targetCourtId);
    if (!canCustomizeOrder || dragId === targetId) return;

    const nextFavorites = [...favoritesRef.current];
    const fromIndex = nextFavorites.findIndex(id => parseInt(id) === dragId);
    const toIndex = nextFavorites.findIndex(id => parseInt(id) === targetId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const [moved] = nextFavorites.splice(fromIndex, 1);
    nextFavorites.splice(toIndex, 0, moved);
    favoritesRef.current = nextFavorites;
    dragOrderChangedRef.current = true;
    setFavoritesState(nextFavorites);
  }, [canCustomizeOrder]);

  const handleDragStart = (event, courtId) => {
    if (!canCustomizeOrder || !isEditingOrder) return;
    const courtElement = event.currentTarget.closest('[data-court-id]');
    const rect = courtElement?.getBoundingClientRect();
    if (!rect) return;

    const parsedCourtId = parseInt(courtId);
    setDraggingCourtId(parsedCourtId);
    setDragMetrics({
      pointerX: event.clientX,
      pointerY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height
    });
    dragOrderChangedRef.current = false;
  };

  const handleDragMove = useCallback((event) => {
    if (!draggingCourtId) return;
    event.preventDefault();
    setDragMetrics(prev => prev ? {
      ...prev,
      pointerX: event.clientX,
      pointerY: event.clientY
    } : prev);
    const target = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest('[data-court-id]');
    const targetCourtId = target?.getAttribute('data-court-id');
    if (targetCourtId) reorderFavorite(draggingCourtId, targetCourtId);
  }, [draggingCourtId, reorderFavorite]);

  const handleDragEnd = useCallback(() => {
    if (dragOrderChangedRef.current) setFavorites(favoritesRef.current);
    setDraggingCourtId(null);
    setDragMetrics(null);
    dragOrderChangedRef.current = false;
  }, [setFavorites]);

  const handleOrderEditToggle = () => {
    if (isEditingOrder && dragOrderChangedRef.current) {
      setFavorites(favoritesRef.current);
      dragOrderChangedRef.current = false;
    }
    setIsEditingOrder(current => !current);
    setDraggingCourtId(null);
    setDragMetrics(null);
  };

  useEffect(() => {
    if (!draggingCourtId) return undefined;

    const handleWindowPointerMove = (event) => handleDragMove(event);
    const handleWindowPointerEnd = () => handleDragEnd();

    window.addEventListener('pointermove', handleWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', handleWindowPointerEnd);
    window.addEventListener('pointercancel', handleWindowPointerEnd);

    return () => {
      window.removeEventListener('pointermove', handleWindowPointerMove);
      window.removeEventListener('pointerup', handleWindowPointerEnd);
      window.removeEventListener('pointercancel', handleWindowPointerEnd);
    };
  }, [draggingCourtId, handleDragEnd, handleDragMove]);

  // Update period when selected day changes (weekday vs weekend)
  useEffect(() => {
    if (isDesktop) return; // Desktop shows all times
    const targetDate = availableDates[selectedDay];
    if (!targetDate) return;
    const [year, month, day] = targetDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    setActivePeriodIndex(isWeekend ? periodPrefs.weekend : periodPrefs.weekday);
  }, [selectedDay, availableDates, periodPrefs, isDesktop]);

  const searchResults = useMemo(() => {
    const nonFavorites = allCourts.filter(c => !favorites.includes(parseInt(c.siteId)));

    // If search query, filter by name
    if (searchQuery && searchQuery.length >= 2) {
      const q = normalizeCourtSearchValue(searchQuery);
      return nonFavorites
        .filter(c => normalizeCourtSearchValue(c.siteName).includes(q))
        .slice(0, 10);
    }

    // If location available, show top 10 closest
    if (userLocation) {
      return nonFavorites
        .filter(c => c.distanceInKm)
        .slice(0, 10);
    }

    return [];
  }, [allCourts, favorites, searchQuery, userLocation]);

  // Loading
  if (isLoading || loadFailed) {
    return (
      <Box className="my-courts-view my-courts-loading-view">
        <CourtCardsSkeleton
          variant="my-courts"
          isFrench={isFrench}
          hasError={loadFailed || isOffline}
          isOffline={isOffline}
          onRetry={() => setLoadAttempt(current => current + 1)}
        />
      </Box>
    );
  }

  // Not logged in
  if (!auth) {
    return (
      <Box className="my-courts-state" sx={{
        p: 4,
        textAlign: 'center',
        maxWidth: 360,
        mx: 'auto',
        mt: 6,
        animation: `${fadeInUp} 0.6s ease-out`
      }}>
        <Box sx={{
          width: 80, height: 80, mx: 'auto', mb: 3,
          borderRadius: '50%',
          background: 'var(--cc-warning-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <StarBorderIcon sx={{ fontSize: 40, color: 'var(--cc-star)' }} />
        </Box>
        <Typography sx={{
          color: 'var(--cc-text)',
          fontSize: '1.5rem',
          fontWeight: 700,
          mb: 1,
          letterSpacing: '-0.02em'
        }}>
          {t.signIn}
        </Typography>
        <Typography sx={{ color: 'var(--cc-text-soft)', mb: 4, lineHeight: 1.5 }}>
          {t.signInDesc}
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/login')}
          sx={{
            bgcolor: 'var(--cc-accent-strong)',
            color: 'var(--cc-on-accent)',
            fontWeight: 700,
            px: 5, py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': { bgcolor: 'var(--cc-accent-dark)', transform: 'translateY(-2px)' },
            transition: 'all 0.2s ease'
          }}
        >
          {t.login}
        </Button>
      </Box>
    );
  }

  // No favorites
  if (favoriteCourts.length === 0) {
    return (
      <Box className="my-courts-state" sx={{ p: 3, animation: `${fadeInUp} 0.5s ease-out` }}>
        <Typography sx={{
          color: 'var(--cc-text)',
          fontSize: '1.75rem',
          fontWeight: 800,
          letterSpacing: '-0.03em',
          mb: 4
        }}>
          {t.myCourts}
        </Typography>

        <Box sx={{
          textAlign: 'center',
          py: 6,
          px: 3,
          borderRadius: '20px',
          background: 'var(--cc-surface)',
          border: '1px solid var(--cc-border)'
        }}>
          <StarBorderIcon sx={{ fontSize: 56, color: 'var(--cc-star)', mb: 2 }} />
          <Typography sx={{ color: 'var(--cc-text)', fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>
            {t.noFavorites}
          </Typography>
          <Typography sx={{ color: 'var(--cc-text-soft)', mb: 3, maxWidth: 280, mx: 'auto', lineHeight: 1.5 }}>
            {t.addFirst}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
            sx={{
              color: 'var(--cc-accent-dark)',
              border: '2px solid var(--cc-accent)',
              borderRadius: '10px',
              px: 3, py: 1,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'var(--cc-hover)', borderColor: 'var(--cc-accent-dark)' }
            }}
          >
            {t.addCourts}
          </Button>
        </Box>

        <AddCourtDialog
          open={showAddDialog}
          onClose={() => { setShowAddDialog(false); setSearchQuery(''); }}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchResults={searchResults}
          handleAddFavorite={handleAddFavorite}
          t={t}
          isFrench={isFrench}
          onGetLocation={handleGetLocation}
          isLoadingLocation={isLoadingLocation}
          hasLocation={!!userLocation}
        />
      </Box>
    );
  }

  // Main view
  return (
    <Box className={`my-courts-view${isEditingOrder ? ' is-order-editing' : ''}`} sx={{
      p: { xs: 1.5, md: 3, lg: 4 },
      pb: 4,
      minHeight: { xs: 'calc(100vh - 16vh)', md: 'calc(100vh - 14vh)' },
      maxWidth: 1100,
      mx: 'auto',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, md: 3 },
        animation: `${fadeInUp} 0.4s ease-out`
      }}>
        <Typography sx={{
          color: 'var(--cc-text)',
          fontSize: '1.75rem',
          fontWeight: 800,
          letterSpacing: '-0.03em'
        }}>
          {t.myCourts}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canCustomizeOrder && (
            <IconButton
              aria-label={isEditingOrder ? t.done : t.editOrder}
              onClick={handleOrderEditToggle}
              sx={{
                bgcolor: isEditingOrder ? 'var(--cc-accent-soft)' : 'var(--cc-surface)',
                border: isEditingOrder ? '1px solid var(--cc-accent)' : '1px solid var(--cc-border)',
                '&:hover': { bgcolor: 'var(--cc-hover)' }
              }}
            >
              {isEditingOrder ? (
                <CheckIcon sx={{ color: 'var(--cc-accent-dark)' }} />
              ) : (
                <EditIcon sx={{ color: 'var(--cc-text-soft)' }} />
              )}
            </IconButton>
          )}
          {!isDesktop && (
            <IconButton
              onClick={() => setShowPrefsPanel(prev => !prev)}
              sx={{
                bgcolor: 'var(--cc-surface)',
                '&:hover': { bgcolor: 'var(--cc-hover)' }
              }}
            >
              <SettingsIcon sx={{ color: 'var(--cc-text-soft)' }} />
            </IconButton>
          )}
          <IconButton
            onClick={() => setShowAddDialog(true)}
            sx={{
              bgcolor: 'var(--cc-surface)',
              '&:hover': { bgcolor: 'var(--cc-hover)' }
            }}
          >
            <AddIcon sx={{ color: 'var(--cc-text-soft)' }} />
          </IconButton>
        </Box>
      </Box>

      <AvailabilityLegend isFrench={isFrench} />

      {/* Day selector */}
      <Box sx={{
        display: 'flex',
        justifyContent: { xs: 'flex-start', md: 'center' },
        gap: { xs: 0.75, md: 1.5 },
        mb: { xs: 2, md: 3 },
        animation: `${fadeInUp} 0.5s ease-out`,
        mx: 0,
        px: { xs: 0.5, md: 0 },
        width: '100%',
        overflowX: { xs: 'auto', md: 'visible' },
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}>
        {availableDates.map((date, idx) => {
          const dateParts = formatDateParts(date, isFrench, todayDateKey);
          const isSelected = selectedDay === idx;
          return (
            <Box
              key={date}
              data-date={date}
              onClick={() => setSelectedDay(idx)}
              sx={{
                px: { xs: 1.25, md: 2 },
                py: { xs: 0.75, md: 1.5 },
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                bgcolor: isSelected ? 'var(--cc-accent-strong)' : 'var(--cc-surface)',
                border: isSelected ? '1px solid var(--cc-accent-strong)' : '1px solid var(--cc-border)',
                '&:hover': {
                  bgcolor: isSelected ? 'var(--cc-accent-dark)' : 'var(--cc-hover)',
                  transform: 'translateY(-2px)'
                },
                textAlign: 'center',
                minWidth: { xs: 48, md: 60 },
                flexShrink: 0,
                position: 'relative',
                overflow: 'hidden',
                ...(dateParts.isToday && {
                  borderColor: isSelected ? 'var(--cc-accent)' : 'var(--cc-star)',
                  boxShadow: isSelected
                    ? 'var(--cc-shadow-soft)'
                    : 'inset 0 0 0 1px var(--cc-star)'
                })
              }}
            >
              {dateParts.isToday && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  bgcolor: isSelected ? 'var(--cc-on-accent)' : 'var(--cc-star)',
                  opacity: isSelected ? 0.75 : 0.9
                }} />
              )}
              {dateParts.isToday && isSelected && (
                <Box sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: -1,
                  transform: 'translateX(-50%)',
                  px: 0.6,
                  py: 0.14,
                  borderRadius: '999px',
                  bgcolor: 'var(--cc-on-accent)',
                  color: 'var(--cc-text)',
                  fontSize: { xs: '0.4rem', md: '0.45rem' },
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  lineHeight: 1,
                  pointerEvents: 'none'
                }}>
                  {t.todayShort.toUpperCase()}
                </Box>
              )}
              <Typography sx={{
                color: isSelected ? 'var(--cc-on-accent)' : 'var(--cc-text-soft)',
                fontWeight: 600,
                fontSize: { xs: '0.6rem', md: '0.65rem' },
                letterSpacing: '0.05em',
                lineHeight: 1.2
              }}>
                {dateParts.dayName}
              </Typography>
              <Typography sx={{
                color: isSelected ? 'var(--cc-on-accent)' : 'var(--cc-text)',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                lineHeight: 1.1
              }}>
                {dateParts.dayNum}
              </Typography>
              <Typography sx={{
                color: isSelected ? 'var(--cc-on-accent)' : 'var(--cc-text-soft)',
                fontWeight: 500,
                fontSize: { xs: '0.55rem', md: '0.6rem' },
                letterSpacing: '0.03em',
                lineHeight: 1.2
              }}>
                {dateParts.month}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {showPrefsPanel && !isDesktop && (
        <AvailabilityPrefsPanel
          prefs={periodPrefs}
          onSave={(newPrefs) => {
            setPeriodPrefs(newPrefs);
            // Set active period based on current day
            const today = new Date();
            const isWeekend = today.getDay() === 0 || today.getDay() === 6;
            setActivePeriodIndex(isWeekend ? newPrefs.weekend : newPrefs.weekday);
            setShowPrefsPanel(false);
            // Save to Firestore
            const user = firebase.auth().currentUser;
            if (user) {
              firebase.firestore().collection('userPreferences').doc(user.uid).set({
                myCourtsPrefs: newPrefs
              }, { merge: true }).catch((e) => console.error('Error saving prefs:', e));
            }
          }}
          onClose={() => setShowPrefsPanel(false)}
          t={t}
        />
      )}

      <Box sx={{ pb: 1 }}>
        {isEditingOrder && (
          <Typography sx={{
            color: 'var(--cc-text-soft)',
            fontSize: '0.78rem',
            fontWeight: 600,
            mb: 1.25,
            textAlign: 'center'
          }}>
            {t.dragToReorder}
          </Typography>
        )}
        {/* Period navigation - only on mobile */}
        {!isDesktop && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1
          }}>
            {activePeriodIndex > 0 ? (
              <Button
                size="small"
                onClick={() => setActivePeriodIndex(prev => prev - 1)}
                startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  color: 'var(--cc-accent-dark)',
                  border: '1px solid var(--cc-border)',
                  borderRadius: '10px',
                  px: 1.5,
                  '&:hover': { bgcolor: 'var(--cc-hover)' }
                }}
              >
                {t.earlier}
              </Button>
            ) : (
              <Box sx={{ width: 90 }} />
            )}
            <Typography sx={{ color: 'var(--cc-text-soft)', fontSize: '0.8rem', fontWeight: 600 }}>
              {t[PERIODS[activePeriodIndex]?.labelKey || 'afternoon']}
            </Typography>
            {activePeriodIndex < PERIODS.length - 1 ? (
              <Button
                size="small"
                onClick={() => setActivePeriodIndex(prev => prev + 1)}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none',
                  color: 'var(--cc-accent-dark)',
                  border: '1px solid var(--cc-border)',
                  borderRadius: '10px',
                  px: 1.5,
                  '&:hover': { bgcolor: 'var(--cc-hover)' }
                }}
              >
                {t.later}
              </Button>
            ) : (
              <Box sx={{ width: 90 }} />
            )}
          </Box>
        )}
        {/* Court rows */}
        {favoriteCourts.map((court, courtIdx) => {
          const courtWindowTimes = getWindowTimesForCourt(court.courtTimes);
          const isDraggingCourt = draggingCourtId === parseInt(court.siteId);
          return (
          <Box
            key={court.siteId}
            data-court-id={court.siteId}
            className={isEditingOrder ? 'my-courts-reorder-card' : undefined}
            sx={{
              mb: { xs: 1.5, md: 2.5 },
              animation: isEditingOrder ? 'none' : `${fadeInUp} ${0.4 + courtIdx * 0.08}s ease-out`,
              animationFillMode: 'both',
              minWidth: isDesktop ? 'auto' : courtWindowTimes.length * TIME_SLOT_WIDTH + Math.max(0, courtWindowTimes.length - 1) * TIME_SLOT_GAP,
              minHeight: isDraggingCourt && dragMetrics ? dragMetrics.height : undefined,
              opacity: isDraggingCourt && !dragMetrics ? 0.72 : 1,
              transition: isEditingOrder ? 'min-height 0.12s ease, opacity 0.15s ease' : undefined
            }}
          >
            <Box sx={{
              ...(isDraggingCourt && dragMetrics && {
                position: 'fixed',
                left: dragMetrics.pointerX - dragMetrics.offsetX,
                top: dragMetrics.pointerY - dragMetrics.offsetY,
                width: dragMetrics.width,
                zIndex: 1400,
                pointerEvents: 'none',
                transform: 'scale(1.015) rotate(-0.35deg)'
              }),
              p: { xs: 1.5, md: 2.5 },
              borderRadius: '14px',
              bgcolor: 'var(--cc-surface)',
              border: isDraggingCourt
                ? '1px solid var(--cc-accent)'
                : '1px solid var(--cc-border)',
              boxShadow: isDraggingCourt
                ? 'var(--cc-shadow-raised), 0 0 0 1px var(--cc-focus)'
                : 'var(--cc-shadow-soft)',
              transition: isDraggingCourt ? 'box-shadow 0.18s ease, transform 0.18s ease' : 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'var(--cc-surface)',
                borderColor: 'var(--cc-accent)'
              }
            }}>
              {/* Court info */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1, md: 1.5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0
                  }}
                >
                  {isEditingOrder && (
                    <IconButton
                      size="small"
                      aria-label={t.editOrder}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.setPointerCapture?.(event.pointerId);
                        handleDragStart(event, court.siteId);
                      }}
                      sx={{
                        p: 0.25,
                        ml: -0.5,
                        color: 'var(--cc-text-soft)',
                        cursor: isDraggingCourt ? 'grabbing' : 'grab',
                        touchAction: 'none',
                        '&:hover': { color: 'var(--cc-accent-dark)', bgcolor: 'var(--cc-hover)' }
                      }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                  <StarIcon sx={{ fontSize: 16, color: 'var(--cc-star)' }} />
                  <Typography sx={{
                    color: 'var(--cc-text)',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {court.siteName.replace('Parc ', '').replace('terrain de sport', '').replace(', terrain de sport', '').replace('Terrains sportifs du parc ', '').trim()}
                  </Typography>
                  {court.siteGoogleMapsLink && (
                    <IconButton
                      size="small"
                      onClick={() => window.open(court.siteGoogleMapsLink, '_blank')}
                      sx={{ p: 0.3, color: 'var(--cc-text-soft)', '&:hover': { color: 'var(--cc-accent-dark)' } }}
                    >
                      <PlaceIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
                {!isEditingOrder && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleRemoveFavorite(e, court.siteId)}
                    sx={{ p: 0.3, color: 'var(--cc-text-soft)', '&:hover': { color: 'var(--cc-danger)' } }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>

              {/* Time labels per court */}
              <Box sx={{
                display: 'flex',
                gap: `${TIME_SLOT_GAP}px`,
                mb: 0.75,
                justifyContent: isDesktop ? 'flex-start' : (
                  activePeriodIndex === 0 ? 'flex-end' : activePeriodIndex === 2 ? 'flex-start' : 'center'
                )
              }}>
                {courtWindowTimes.map((time) => (
                  <Typography
                    key={time}
                    sx={{
                      width: isDesktop ? 'auto' : TIME_SLOT_WIDTH,
                      flex: isDesktop ? 1 : 'none',
                      textAlign: 'center',
                      fontSize: '0.6rem',
                      color: 'var(--cc-text-soft)',
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {time}
                  </Typography>
                ))}
              </Box>

              {/* Time grid */}
              <Box sx={{
                display: 'flex',
                gap: `${TIME_SLOT_GAP}px`,
                position: 'relative',
                justifyContent: isDesktop ? 'flex-start' : (
                  activePeriodIndex === 0 ? 'flex-end' : activePeriodIndex === 2 ? 'flex-start' : 'center'
                )
              }}>
                {(() => {
                // Only show now marker for today (selectedDay === 0)
                if (selectedDay !== 0) return null;
                const markerPercent = getNowMarkerPercent(courtWindowTimes);
                if (markerPercent === null) return null;
                return (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${markerPercent}%`,
                        top: -6,
                        bottom: -6,
                        width: 2,
                        borderRadius: 2,
                        bgcolor: 'var(--cc-star)',
                        boxShadow: '0 0 10px var(--cc-star)',
                        pointerEvents: 'none'
                      }}
                    />
                  );
                })()}
                {courtWindowTimes.map(time => {
                  const slots = court.timeSlots[time];
                  const isAvailable = slots && slots.length > 0;
                  const isSelected = selectedSlot && selectedSlot.court.siteId === court.siteId && selectedSlot.time === time;
                  const availabilityColor = isAvailable ? getAvailabilityColor(slots.length) : null;

                  return (
                    <Box
                      key={time}
                      onClick={(event) => {
                        if (!isEditingOrder) handleTileClick(event, court, time);
                      }}
                      sx={{
                        width: isDesktop ? 'auto' : TIME_SLOT_WIDTH,
                        flex: isDesktop ? 1 : 'none',
                        height: { xs: 36, md: 44 },
                        borderRadius: '6px',
                        bgcolor: isAvailable
                          ? availabilityColor.bg
                          : 'transparent',
                        cursor: isAvailable && !isEditingOrder ? 'pointer' : 'default',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: isAvailable ? `1px solid ${availabilityColor.border}` : '1px solid var(--cc-avail-0-border)',
                        position: 'relative',
                        boxSizing: 'border-box',
                        ...(isAvailable && !isEditingOrder && {
                          '&:hover': {
                            bgcolor: availabilityColor.bg,
                            transform: 'scale(1.08)',
                            zIndex: 1,
                            animation: `${pulseGlow} 1.5s infinite`
                          }
                        }),
                        ...(isSelected && {
                          boxShadow: '0 0 0 2px var(--cc-avail-ring)',
                          bgcolor: 'var(--cc-avail-3-bg)'
                        }),
                        ...(!isAvailable && {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: 3,
                            right: 3,
                            top: '50%',
                            height: 6,
                            transform: 'translateY(-50%)',
                            borderRadius: 6,
                            background: 'var(--cc-avail-0-dash)'
                          }
                        })
                      }}
                    >
                      {isAvailable && (
                        <Typography sx={{
                          color: 'var(--cc-avail-ink)',
                          fontSize: '0.65rem',
                          fontWeight: 700
                        }}>
                          {slots.length}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

          </Box>
        );})}
      </Box>

      {isEditingOrder && !isDesktop && (
        <Button
          className="my-courts-order-save"
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={handleOrderEditToggle}
          aria-label={t.saveOrder}
        >
          {t.saveOrder}
        </Button>
      )}

      {/* Hint */}
      <AddCourtDialog
        open={showAddDialog}
        onClose={() => { setShowAddDialog(false); setSearchQuery(''); }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchResults={searchResults}
        handleAddFavorite={handleAddFavorite}
        t={t}
        isFrench={isFrench}
        onGetLocation={handleGetLocation}
        isLoadingLocation={isLoadingLocation}
        hasLocation={!!userLocation}
      />
      <Popover
        open={Boolean(selectedSlot)}
        anchorEl={selectedSlot?.anchorEl}
        onClose={closeSelection}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            bgcolor: 'var(--cc-surface)',
            color: 'var(--cc-text)',
            p: 1.5,
            mt: 0.5,
            minWidth: 180,
            boxShadow: 'var(--cc-shadow-raised)'
          }
        }}
      >
        {selectedSlot && (() => {
          // Sort slots numerically by extracting number from facility name
          const sortedSlots = [...selectedSlot.slots].sort((a, b) => {
            const numA = parseInt((a.facility || '').match(/\d+/)?.[0] || '0', 10);
            const numB = parseInt((b.facility || '').match(/\d+/)?.[0] || '0', 10);
            return numA - numB;
          });
          const useColumns = sortedSlots.length > 6;
          const rowCount = Math.ceil(sortedSlots.length / 2);

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--cc-text-soft)', mb: 0.25 }}>
                {selectedSlot.time} · {selectedSlot.court.siteName.replace('Parc ', '').replace('terrain de sport', '').replace(', terrain de sport', '').trim()}
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: useColumns ? '1fr 1fr' : '1fr',
                gridTemplateRows: useColumns ? `repeat(${rowCount}, auto)` : 'auto',
                gridAutoFlow: useColumns ? 'column' : 'row',
                gap: 0.75
              }}>
                {sortedSlots.map((slot, idx) => (
                  <Button
                    key={`${slot.facility}-${idx}`}
                    fullWidth
                    size="small"
                    onClick={() => handleCourtSelect(slot)}
                    sx={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      color: slot.isPredictive ? 'var(--cc-premium)' : 'var(--cc-accent-dark)',
                      bgcolor: slot.isPredictive ? 'var(--cc-premium-soft)' : 'var(--cc-accent-soft)',
                      border: slot.isPredictive ? '1px dashed var(--cc-premium)' : '1px solid var(--cc-accent)',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      '&:hover': { bgcolor: slot.isPredictive ? 'var(--cc-premium-soft)' : 'var(--cc-hover)' }
                    }}
                  >
                    {slot.isPredictive && <span style={{ marginRight: 6 }}>★</span>}
                    {slot.facility || `${t.chooseCourt} ${idx + 1}`}
                  </Button>
                ))}
              </Box>
            </Box>
          );
        })()}
      </Popover>
      <Dialog
        open={showPredictiveUpgrade}
        onClose={() => setShowPredictiveUpgrade(false)}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: 'var(--cc-premium)' }}>
          ★ {t.predictiveUpgradeTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--cc-text-soft)', lineHeight: 1.55, fontSize: '0.9rem' }}>
            {t.predictiveUpgradeBody}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowPredictiveUpgrade(false)}
            sx={{
              color: 'var(--cc-text-soft)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.close}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowPredictiveUpgrade(false);
              navigate('/profile');
            }}
            sx={{
              bgcolor: 'var(--cc-premium)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'var(--cc-premium)' }
            }}
          >
            {t.upgrade}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(predictiveConfirm)}
        onClose={() => setPredictiveConfirm(null)}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: 'var(--cc-premium)' }}>
          ★ {t.predictiveTitle}
        </DialogTitle>
        <DialogContent>
          {predictiveConfirm && (
            <Typography sx={{ color: 'var(--cc-text-soft)', lineHeight: 1.55, fontSize: '0.9rem' }}>
              {predictiveConfirm.facility} — {predictiveConfirm.time}
              <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
                {t.predictiveNotice}
              </Box>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPredictiveConfirm(null)}
            sx={{
              color: 'var(--cc-text-soft)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const url = predictiveConfirm?.url;
              setPredictiveConfirm(null);
              if (url) window.open(url, '_blank');
            }}
            sx={{
              bgcolor: 'var(--cc-premium)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'var(--cc-premium)' }
            }}
          >
            {t.openPredictive}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showFavoriteLimitDialog}
        onClose={() => setShowFavoriteLimitDialog(false)}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t.favoriteLimitTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--cc-text-soft)', lineHeight: 1.55 }}>
            {t.favoriteLimitMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowFavoriteLimitDialog(false)}
            sx={{
              color: 'var(--cc-text-soft)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.close}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowFavoriteLimitDialog(false);
              navigate('/profile');
            }}
            sx={{
              bgcolor: 'var(--cc-accent)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: 'var(--cc-accent-dark)' }
            }}
          >
            {t.upgrade}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Period selector panel for mobile
const AvailabilityPrefsPanel = ({ prefs, onSave, onClose, t }) => {
  const [weekdayPeriod, setWeekdayPeriod] = useState(prefs.weekday || 1);
  const [weekendPeriod, setWeekendPeriod] = useState(prefs.weekend || 1);

  const periods = [
    { key: 'morning', label: t.morning },
    { key: 'afternoon', label: t.afternoon },
    { key: 'evening', label: t.evening }
  ];

  const handleSave = () => {
    onSave({ weekday: weekdayPeriod, weekend: weekendPeriod });
  };

  return (
    <Box sx={{
      mb: 2,
      p: 2,
      borderRadius: '16px',
      bgcolor: 'var(--cc-surface)',
      border: '1px solid var(--cc-border)'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ color: 'var(--cc-text)', fontWeight: 700, fontSize: '0.95rem' }}>
          {t.availabilitySetup}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'var(--cc-text-soft)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Weekday selection */}
      <Typography sx={{ color: 'var(--cc-text-soft)', fontSize: '0.8rem', mb: 1 }}>
        {t.weekdays}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
        {periods.map((period, idx) => (
          <Button
            key={`weekday-${period.key}`}
            fullWidth
            onClick={() => setWeekdayPeriod(idx)}
            sx={{
              textTransform: 'none',
              py: 1,
              fontSize: '0.8rem',
              color: weekdayPeriod === idx ? 'var(--cc-on-accent)' : 'var(--cc-text-soft)',
              bgcolor: weekdayPeriod === idx ? 'var(--cc-accent)' : 'var(--cc-surface-muted)',
              border: weekdayPeriod === idx ? '1px solid var(--cc-accent)' : '1px solid var(--cc-border)',
              borderRadius: '8px',
              fontWeight: weekdayPeriod === idx ? 600 : 500,
              '&:hover': {
                bgcolor: weekdayPeriod === idx ? 'var(--cc-accent-dark)' : 'var(--cc-hover)'
              }
            }}
          >
            {period.label}
          </Button>
        ))}
      </Box>

      {/* Weekend selection */}
      <Typography sx={{ color: 'var(--cc-text-soft)', fontSize: '0.8rem', mb: 1 }}>
        {t.weekends}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
        {periods.map((period, idx) => (
          <Button
            key={`weekend-${period.key}`}
            fullWidth
            onClick={() => setWeekendPeriod(idx)}
            sx={{
              textTransform: 'none',
              py: 1,
              fontSize: '0.8rem',
              color: weekendPeriod === idx ? 'var(--cc-on-accent)' : 'var(--cc-text-soft)',
              bgcolor: weekendPeriod === idx ? 'var(--cc-accent)' : 'var(--cc-surface-muted)',
              border: weekendPeriod === idx ? '1px solid var(--cc-accent)' : '1px solid var(--cc-border)',
              borderRadius: '8px',
              fontWeight: weekendPeriod === idx ? 600 : 500,
              '&:hover': {
                bgcolor: weekendPeriod === idx ? 'var(--cc-accent-dark)' : 'var(--cc-hover)'
              }
            }}
          >
            {period.label}
          </Button>
        ))}
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleSave}
        sx={{
          textTransform: 'none',
          py: 1,
          bgcolor: 'var(--cc-accent)',
          borderRadius: '10px',
          fontWeight: 600,
          '&:hover': { bgcolor: 'var(--cc-accent-dark)' }
        }}
      >
        {t.save}
      </Button>
    </Box>
  );
};

const AddCourtDialog = ({ open, onClose, searchQuery, setSearchQuery, searchResults, handleAddFavorite, t, isFrench, onGetLocation, isLoadingLocation, hasLocation }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { maxHeight: '70vh' } }}
  >
    <DialogTitle sx={{ color: 'var(--cc-text)', fontWeight: 700, pb: 1 }}>
      {t.addCourts}
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: 'var(--cc-text-soft)' }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <TextField
        fullWidth
        placeholder={t.searchCourts}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoFocus
        sx={{
          mt: 1,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'var(--cc-surface)',
            borderRadius: '12px',
            color: 'var(--cc-text)',
            '& fieldset': { borderColor: 'var(--cc-border)' },
            '&:hover fieldset': { borderColor: 'var(--cc-accent)' },
            '&.Mui-focused fieldset': { borderColor: 'var(--cc-accent)' }
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'var(--cc-text-soft)' }} />
            </InputAdornment>
          )
        }}
      />
      {!hasLocation && (
        <Button
          fullWidth
          startIcon={<MyLocationIcon />}
          onClick={onGetLocation}
          disabled={isLoadingLocation}
          sx={{
            mt: 1.5,
            py: 1,
            textTransform: 'none',
            color: 'var(--cc-accent-dark)',
            bgcolor: 'var(--cc-accent-soft)',
            borderRadius: '10px',
            '&:hover': { bgcolor: 'var(--cc-hover)' },
            '&.Mui-disabled': { color: 'var(--cc-text-soft)' }
          }}
        >
          {isLoadingLocation ? t.locating : t.useMyLocation}
        </Button>
      )}
      {searchQuery.length < 2 && hasLocation && searchResults.length > 0 && (
        <Typography sx={{ color: 'var(--cc-text-soft)', fontSize: '0.8rem', fontWeight: 600, mt: 2, mb: 0.5 }}>
          {isFrench ? "Les plus proches" : "Closest to you"}
        </Typography>
      )}
      <List sx={{ mt: (searchQuery.length < 2 && hasLocation && searchResults.length > 0) ? 0 : 2 }}>
        {searchResults.map(court => (
          <ListItem
            key={court.siteId}
            sx={{
              borderRadius: '12px',
              mb: 0.5,
              '&:hover': { bgcolor: 'var(--cc-hover)' }
            }}
          >
            <ListItemText
              primary={court.siteName}
              secondary={court.distanceInKm ? `${court.distanceInKm} km` : null}
              primaryTypographyProps={{ color: 'var(--cc-text)', fontWeight: 500 }}
              secondaryTypographyProps={{ color: 'var(--cc-text-soft)' }}
            />
            <ListItemSecondaryAction>
              <IconButton onClick={() => handleAddFavorite(court.siteId)} sx={{ color: 'var(--cc-accent)' }}>
                <AddIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {searchQuery.length >= 2 && searchResults.length === 0 && (
          <Typography sx={{ textAlign: 'center', color: 'var(--cc-text-soft)', py: 4 }}>
            {isFrench ? "Aucun résultat" : "No results"}
          </Typography>
        )}
        {searchQuery.length < 2 && !hasLocation && (
          <Typography sx={{ textAlign: 'center', color: 'var(--cc-text-soft)', py: 4 }}>
            {isFrench ? "Tapez pour rechercher..." : "Type to search..."}
          </Typography>
        )}
      </List>
    </DialogContent>
  </Dialog>
);

export default PersonalizedCourtsView;
